import React, { useMemo } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';

import { usePartnerEarnOutcomes } from '~/hooks/apis/loyalty/usePartnerTransactionSearch';

import type { PartnerEarnBulkResult } from '~/types/loyaltyContract';

import { downloadFailedPartnerEarnCsv, downloadProcessingFailuresCsv } from '../csvHelpers';

interface Props {
  result: PartnerEarnBulkResult;
  partnerId: string;
  onStartNew: () => void;
}

/** The alert lists a few failures inline; the rest are in the downloadable report. */
const MAX_LISTED_FAILURES = 5;

interface CountProps {
  value: number;
  label: string;
  color: string;
}

function Count({ value, label, color }: CountProps) {
  return (
    <Box textAlign="center">
      <Typography variant="h4" color={color}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function ResultStep({ result, partnerId, onStartNew }: Props) {
  const { successCount, alreadyCreditedCount, failedCount, results } = result;
  const hasFailures = failedCount > 0;
  const failedResults = useMemo(() => results.filter((row) => !row.success), [results]);

  // Only rows the service accepted have a transaction to report on.
  const submittedKeys = useMemo(
    () => results.filter((row) => row.success && row.idempotencyKey).map((row) => row.idempotencyKey as string),
    [results],
  );

  const outcomes = usePartnerEarnOutcomes(partnerId, submittedKeys);
  const counts = outcomes.data?.counts;
  const processingFailures = outcomes.data?.failed ?? [];

  return (
    <Box display="flex" flexDirection="column" alignItems="center" py={4}>
      {hasFailures ? (
        <ErrorIcon color="warning" sx={{ fontSize: 64, mb: 2 }} />
      ) : (
        <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
      )}

      <Typography variant="h5" gutterBottom>
        {hasFailures ? 'Partner earn submitted with errors' : 'Partner earn submitted'}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Credits are processed asynchronously, so these counts describe what was accepted.
      </Typography>

      <Stack direction="row" spacing={4} mt={1} mb={3}>
        <Count value={successCount} label="Queued" color="success.main" />
        {alreadyCreditedCount > 0 && <Count value={alreadyCreditedCount} label="Already credited" color="info.main" />}
        {hasFailures && <Count value={failedCount} label="Not accepted" color="error.main" />}
      </Stack>

      {alreadyCreditedCount > 0 && (
        <Alert severity="info" sx={{ mb: 3, maxWidth: 600, width: '100%' }}>
          <Typography variant="body2">
            {alreadyCreditedCount} credit(s) had already been submitted with the same details, so no new points were
            added for them.
          </Typography>
        </Alert>
      )}

      {hasFailures && (
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 600, width: '100%' }}>
          <Typography variant="body2">
            {failedCount} credit(s) were not accepted. Download the error report for details.
          </Typography>
        </Alert>
      )}

      {submittedKeys.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 600, mb: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">Processing outcome</Typography>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => outcomes.refetch()}
              disabled={outcomes.isFetching}
            >
              Refresh
            </Button>
          </Stack>

          {outcomes.isLoading && <CircularProgress size={20} />}

          {outcomes.isError && (
            <Alert severity="error">Could not load processing outcomes: {outcomes.error.message}</Alert>
          )}

          {counts && (
            <>
              <Stack direction="row" spacing={4} justifyContent="center" my={2}>
                <Count value={counts.completed} label="Credited" color="success.main" />
                <Count value={counts.pending} label="Processing" color="text.primary" />
                <Count value={counts.failed} label="Failed" color="error.main" />
              </Stack>

              {counts.pending > 0 && (
                <Alert severity="info" icon={<CircularProgress size={18} />}>
                  <Typography variant="body2">
                    {counts.pending} credit(s) are still being processed. This updates on its own.
                  </Typography>
                </Alert>
              )}

              {counts.failed > 0 && (
                <Alert severity="error">
                  <Typography variant="body2" gutterBottom>
                    {counts.failed} credit(s) were accepted but failed during processing:
                  </Typography>
                  {processingFailures.slice(0, MAX_LISTED_FAILURES).map((transaction) => (
                    <Typography key={transaction.transactionId} variant="caption" display="block">
                      Member {transaction.accountNumber}: {transaction.errorMessage ?? transaction.failureReason}
                    </Typography>
                  ))}
                  {processingFailures.length > MAX_LISTED_FAILURES && (
                    <Typography variant="caption" display="block" mt={1}>
                      Showing {MAX_LISTED_FAILURES} of {processingFailures.length}. Download the report for the rest.
                    </Typography>
                  )}
                </Alert>
              )}
            </>
          )}
        </Box>
      )}

      <Stack direction="row" spacing={2}>
        {hasFailures && failedResults.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadFailedPartnerEarnCsv(failedResults)}
          >
            Download Rejected Rows
          </Button>
        )}
        {processingFailures.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DownloadIcon />}
            onClick={() => downloadProcessingFailuresCsv(processingFailures)}
          >
            Download Failed Rows
          </Button>
        )}
        <Button variant="contained" onClick={onStartNew}>
          Start New Earn as a Partner
        </Button>
      </Stack>
    </Box>
  );
}
